import { IAppComponentProps } from '../../../../components';
import React, { useEffect, useState } from 'react';
import { Button, Card, Checkbox, Col, Form, Modal, Row, Select, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import { getI18n, isExternalUser } from '../../../../utils/Utils';
import { updateSelectedPartner } from '../../IStoredPartnerRecordSlice';
import { useAppDispatch, useAppSelector } from '../../../../main/hooks';
import { filteredEntitlementLookupForSelf } from '../../../../main/entitlementService';
import { getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners } from '../../../../main/__generated__/getSelfEntitlementResultByFilter';
import { ECAP_USER_ENTITLEMENT_BY_USERID } from '../../../../main/queries';
import { getEcapUserEntitlementResultByUserId } from '../../../../main/__generated__/getEcapUserEntitlementResultByUserId';
import { DocumentType } from '../../../../components/DocumentTypes';
import { BmwCompanyCode } from '../../../../components/BmwCompanyCode';
import { isTemplateDocumentLoading } from '../../IStoredTemplateDocumentLoadingSlice';
import { setDocumentMode, setTemplateMode } from '../../IStoredDocumentRecordSlice';
import RenderIfEntitled from '../../../../components/EntitlementComponents';
import {useNavigate} from "react-router-dom";
import _ from "lodash";
import {DiffOutlined, ReloadOutlined, SearchOutlined} from "@ant-design/icons";

type TemplateDocumentSearchFormProps = {
    onSearch: any;
} & IAppComponentProps;

const template_Entitlement = 'create_dp@ecap';

const TemplateDocumentSearchForm: React.FunctionComponent<TemplateDocumentSearchFormProps> = (props) => {
    const [checked, setChecked] = React.useState(false);

    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const [selectOptions, setSelectOption] = useState<
        getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners[]
    >([]);

    const [partnerNo, setPartnerNo] = useState<string>(undefined);
    const intl = useIntl();
    const navigate = useNavigate();
    const { loading } = useAppSelector((state) => state.templateDocumentLoading);

    const removeDuplicated = (data: getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners[]) =>
        Array.from(
            data
                .reduce((acc, item) => (item && item['partnerId'] && acc.set(item['partnerId'], item), acc), new Map())
                .values()
        );

    const searchPartner = (partnerName) => {
        Promise.resolve(filteredEntitlementLookupForSelf(template_Entitlement, partnerName, 1, 200))
            .then((response) => {
                const searchOption = removeDuplicated(response.getSelfEntitlementResultByFilter.partners);
                setSelectOption(searchOption);
                dispatch(isTemplateDocumentLoading(false));
            })
            .catch((err) => {
                console.error(err);
                dispatch(isTemplateDocumentLoading(false));
            });
    };

    const findSelectedPartner = (partnerNo) => {
        return selectOptions.find((item) => {
            if (item.partnerNo === partnerNo) {
                return item;
            }
        });
    };

    const handleSearch = _.debounce(function (value: string) {
        dispatch(isTemplateDocumentLoading(true));
        searchPartner(value);
    }, 1000);

    const [getUserEntitlement, {}] = useLazyQuery<getEcapUserEntitlementResultByUserId>(
        ECAP_USER_ENTITLEMENT_BY_USERID,
        {
            variables: { userId: props.currentUser.uid, authentication: localStorage.getItem('id_token') },
            fetchPolicy: 'cache-first',
        }
    );

    const checkPartnerEntitlement = () => {
        if (isExternalUser(props.distinctEntitlements)) {
            dispatch(isTemplateDocumentLoading(true));
            getUserEntitlement()
                .then((response) => {
                    const userEntitlement = response.data?.getEcapUserEntitlementResultByUserId;
                    if (userEntitlement?.partners?.length === 1) {
                        form.setFieldsValue({
                            partnerId: `${userEntitlement.partners[0]?.partnerNo} - ${userEntitlement.partners[0]?.partnerName}`,
                        });
                        setPartnerNo(userEntitlement.partners[0]?.partnerNo);
                    }
                    if (userEntitlement?.partners?.length > 1) {
                        setSelectOption(userEntitlement?.partners);
                    }
                    dispatch(isTemplateDocumentLoading(false));
                })
                .catch((_) => {
                    dispatch(isTemplateDocumentLoading(false));
                });
        }
    };

    const onSearch = () => {
        if (partnerNo) {
            const values = { ...form.getFieldsValue(), partnerNo };
            props.onSearch(values);
        } else {
            props.onSearch(form.getFieldsValue());
        }
    };

    const onClear = () => {
        Modal.confirm({
            title: intl.formatMessage({
                id: 'ecap-template-search-reset-data',
                defaultMessage: 'Reset Input Data',
            }),
            content: intl.formatMessage({
                id: 'ecap-template-search-input-data-warning',
                defaultMessage: 'Input Data will be lost. Do you want to continue?',
            }),
            okText: intl.formatMessage({
                id: 'e-cap-capture-document-cancel-confirm-modal-yes',
                defaultMessage: 'YES',
            }),
            cancelText: intl.formatMessage({
                id: 'e-cap-capture-document-cancel-confirm-modal-no',
                defaultMessage: 'NO',
            }),
            onOk: () => form.resetFields(),
        });
    };

    const onCreate = () => {
        dispatch(setDocumentMode(undefined));
        console.log("Create new template");
        dispatch(setTemplateMode(true));
        navigate('/ecap/template-processing');
    };

    const handleChange = (value) => {
        dispatch(updateSelectedPartner(findSelectedPartner(value)));
        setChecked(!checked);
    };
    const onFinishFailed = (errorInfo: any) => {
        console.log('Failed:', errorInfo);
    };

    useEffect(() => {
        checkPartnerEntitlement();
        onSearch();
    }, [partnerNo]);

    return (
        <>
            <Form
                size={'middle'}
                form={form}
                style={{ width: '2100px' }}
                layout={'vertical'}
                onFinish={onSearch}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                className={'mt100'}

            >
                <Card title={getI18n('management-searchmask-title', 'Search Mask', intl)} size={'default'}>
                    <Row gutter={24} style={{ alignContent: 'inherit', height: '60px', position: 'relative', left: "-30px"}}>

                        <Col span={5}>
                            <BmwCompanyCode
                                currentUser={props.currentUser}
                                intl={props.intl}
                                distinctEntitlements={props.distinctEntitlements}
                            />
                        </Col>
                        <Col span={5}>
                            <Form.Item label={getI18n('partner-overview', 'Partner', intl)} name={'partnerNo'}>
                                <Select
                                    style={{ width: 400 }}
                                    size={'middle'}
                                    showSearch={true}
                                    placeholder={getI18n(
                                        'document-overview-search-message',
                                        'Enter partner number or partner name',
                                        intl
                                    )}
                                    onSearch={handleSearch}
                                    onChange={handleChange}
                                    defaultActiveFirstOption={true}
                                    filterOption={false}
                                    notFoundContent={null}
                                    loading={loading}
                                >
                                    {selectOptions &&
                                        selectOptions.map((item) => (
                                            <Select.Option
                                                key={`${item.partnerId}_${item.partnerNo}`}
                                                value={item.partnerNo}
                                                data-testid={`partnerNo_${item.partnerNo}`}
                                            >
                                                {item.partnerNo} - {item.partnerName}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <DocumentType bmwTypesOnly={true} currentUser={props.currentUser} intl={props.intl} distinctEntitlements={[]} />
                        </Col>
                    </Row>

                    <Row gutter={24} style={{ alignContent: 'inherit', height: '60px',  position: 'relative', left: "-30px" }}>
                        <Col span={5}>
                            <Col span={5} className="mb15 mt05">
                                <Form.Item name={'myTemplates'} valuePropName="checked">
                                    <Checkbox className="mb25 mt35">
                                        {getI18n('ecap-template-filter-mytemplates-fieldname', 'My Templates  ', intl)}
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Col>
                    </Row>
                </Card>
                <Card size="small">
                    <Row gutter={24}>
                        <RenderIfEntitled entitlementToCheck='create_dp@ecap' distinctEntitlements={props.distinctEntitlements} intl={props.intl}>
                            <Tooltip title={getI18n('ecap-template-create-button', 'Create New Template', intl)}>
                                <Button
                                    style={{marginRight:"10px"}}
                                    type={'primary'}
                                    size="large"
                                    className="w-100 mt10"
                                    data-testid={'btn-create'}
                                    onClick={onCreate}
                                    icon={<DiffOutlined />}
                                />
                            </Tooltip>
                            <Tooltip title={getI18n('welcome-search-btton', 'SEARCH', intl)}>
                                <Button
                                    style={{marginRight:"10px"}}
                                    type={'primary'}
                                    htmlType="submit"
                                    size="large"
                                    className="w-100 mt10"
                                    data-testid={'btn-search'}
                                    icon={<SearchOutlined/>}
                                />
                            </Tooltip>
                            <Tooltip title={getI18n('welcome-Clear-button', 'CLEAR', intl)}>
                                <Button
                                    style={{marginRight:"10px"}}
                                    type="primary"
                                    onClick={onClear}
                                    size="large"
                                    className="w-100 mt10"
                                    data-testid={'btn-clear'}
                                    icon={<ReloadOutlined />}
                                />
                            </Tooltip>
                        </RenderIfEntitled>
                    </Row>
                </Card>
            </Form>
        </>
    );
};
export default TemplateDocumentSearchForm;
